import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Location } from 'react-router-dom';

import { getDocumentName, getFragmentData } from '@e-socna/data-layer';
import type { MutationLoginArgs } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import {
  AuthenticationContextProvider_AuthenticatedUser_QueryDocument,
  AuthenticationContextProvider_AuthenticatedUser_Fragment,
} from '../../../contexts/AuthenticationContext';
import type { BackError } from '../../../utils/back-errors.util';

import { credentialsForm2ApiMapper } from '../mappers/credentials.form2api.mapper';

import { useLoginMutation } from './useLoginMutation.hook';

interface LocationState {
  readonly from?: Location;
}

export const useLoginSubmit = () => {
  const [formError, setFormError] = useState<readonly string[] | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { mutate: loginMutation } = useLoginMutation();

  const handleSubmit = useCallback(
    (
      values: MutationLoginArgs,
      {
        setSubmitting,
      }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
    ) => {
      setFormError(null);
      loginMutation(credentialsForm2ApiMapper(values), {
        onError: (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));

          setSubmitting(false);
        },
        onSuccess: (userData) => {
          const authenticationData = getFragmentData(
            AuthenticationContextProvider_AuthenticatedUser_Fragment,
            userData.login,
          );

          queryClient.setQueryData(
            [
              getDocumentName(
                AuthenticationContextProvider_AuthenticatedUser_QueryDocument,
              ),
            ],
            {
              getAuthenticated: authenticationData,
            },
          );
          // HACK : if we don't set a timeout, then, following behavior is buggy :
          // - connect
          // - then disconnect
          // - try to connect again
          // - you'll see that you can access LoggedIn space for a split of second
          // - and then kicked again toward login page
          // - try to connect again => OK
          setTimeout(() => {
            // Send them back to the page they tried to visit when they were
            // redirected to the login page. Use { replace: true } so we don't create
            // another entry in the history stack for the login page.  This means that
            // when they get to the protected page and click the back button, they
            // won't end up back on the login page, which is also really nice for the
            // user experience.
            const from = ((location.state as LocationState | undefined | null)
              ?.from ?? { pathname: '/' }) as Location;
            void navigate(from, { replace: true });
            setSubmitting(false);
          }, 100);
        },
      });
    },
    [loginMutation, navigate, location.state, queryClient],
  );

  return { formError, handleSubmit };
};
